import React from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Descriptions,
  App,
  Spin,
  Alert,
  Modal,
  Form,
  DatePicker,
  Select,
  Input,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  DownloadOutlined,
  ToolOutlined,
  EditOutlined,
  SolutionOutlined,
  DeploymentUnitOutlined,
  PrinterOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import DocumentUpload from '@/components/documents/DocumentUpload';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import { useDocumentsByEntity } from '@/hooks/useDocuments';
import quotationService from '@/services/quotation.service';
import type { Document } from '@/services/document.service';
import * as prApi from '@/services/productionRequest.service';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_PRODUCTION_REQUEST } from '@/constants/approval-entity-types';
import { useAuthStore } from '@/store/authStore';

const PRODUCTION_APPROVAL_FORM_DOC_TYPE = 'ProductionApprovalForm';
const EDITABLE_PR_STATUSES = ['Draft', 'Returned', 'Rejected'];
const PERM_PR_SIGNOFF_ESTIMATION = 'manufacturing.production_request.signoff.estimation';
const PERM_PR_SIGNOFF_OPERATIONS = 'manufacturing.production_request.signoff.operations';
const PERM_PR_APPROVE = 'manufacturing.production_request.approve';

const { Title, Text } = Typography;

function csvCell(v: string | number | undefined | null): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatUser(u?: { first_name?: string; last_name?: string; username?: string }): string {
  if (!u) return '';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.username || '';
}

function apiActionErrorMessage(e: unknown): string {
  const x = e as { response?: { data?: { message?: string } }; message?: string };
  return x.response?.data?.message || x.message || (e as Error).message || 'Request failed';
}

function nilUuid(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return String(iso).replace('T', ' ').slice(0, 19);
}

export default function ProductionRequestsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const perms = user?.permissions || [];
  const canSignoffEstimation = isSuperAdmin || perms.includes(PERM_PR_SIGNOFF_ESTIMATION);
  const canSignoffOperations = isSuperAdmin || perms.includes(PERM_PR_SIGNOFF_OPERATIONS);
  const canApprovePr =
    isSuperAdmin ||
    perms.includes(PERM_PR_APPROVE) ||
    perms.includes('manufacturing.view') ||
    perms.includes('manufacturing.reports.view') ||
    perms.includes('manufacturing.wo.complete');
  const [detailAction, setDetailAction] = React.useState<
    null | 'submit' | 'signoffEst' | 'signoffOps' | 'saveWaiver' | 'approve'
  >(null);
  const [editWaiverOpen, setEditWaiverOpen] = React.useState(false);
  const [waiverForm] = Form.useForm<{
    quotation_id?: string | null;
    external_ref_no?: string | null;
    effective_date?: Dayjs | null;
    contact_person?: string | null;
    po_waiver_reason?: prApi.PoWaiverReason | null;
    po_waiver_reason_detail?: string | null;
    responsibility_notes?: string | null;
  }>();
  const poWaiverReasonWatch = Form.useWatch('po_waiver_reason', waiverForm);
  const { data: departments = [] } = useDepartmentsForSelection();

  const listQ = useQuery({
    queryKey: ['production-requests'],
    queryFn: () => prApi.listProductionRequests(),
    enabled: !routeId,
  });

  const detailQ = useQuery({
    queryKey: ['production-request', routeId],
    queryFn: () => prApi.getProductionRequest(routeId!),
    enabled: Boolean(routeId),
  });

  const docsQ = useDocumentsByEntity('ProductionRequest', routeId ?? '');
  const acceptedQuotationsQ = useQuery({
    queryKey: ['quotations', 'accepted-latest-pr-detail'],
    queryFn: () => quotationService.getAll({ status: 'Accepted', is_latest_revision: true }),
    enabled: editWaiverOpen,
  });
  const acceptedQuotations = acceptedQuotationsQ.data ?? [];

  const usedQuotationIdsOnPrDetailQ = useQuery({
    queryKey: ['production-requests', 'used-quotation-ids'],
    queryFn: () => prApi.listUsedQuotationIdsForProductionRequests(),
    enabled: Boolean(routeId),
  });

  const exportListCsv = () => {
    const rows = listQ.data ?? [];
    const header = [
      'Number',
      'Date',
      'Status',
      'Priority',
      'Source',
      'Quotation',
      'Customer',
      'Warehouse',
      'Requested by',
    ];
    const dataLines = rows.map((r) =>
      [
        csvCell(r.request_number),
        csvCell(String(r.request_date || '').slice(0, 10)),
        csvCell(r.status),
        csvCell(r.priority),
        csvCell(r.source_type),
        csvCell(r.quotation?.quotation_number ?? ''),
        csvCell(r.customer?.customer_name ?? ''),
        csvCell(r.warehouse?.warehouse_name ?? r.warehouse_id ?? ''),
        csvCell(formatUser(r.requester)),
      ].join(',')
    );
    const bom = new Blob([`\uFEFF${[header.join(','), ...dataLines].join('\r\n')}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(bom);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('Exported CSV');
  };

  const columns: ColumnsType<prApi.ProductionRequest> = [
    {
      title: 'Number',
      dataIndex: 'request_number',
      key: 'request_number',
      render: (n: string, r) =>
        r.parent_production_request_id ? (
          <Space size={4} wrap>
            <span>{n}</span>
            <Tag color="purple">Supplementary of {r.parent_request_number ?? 'earlier PR'}</Tag>
          </Space>
        ) : (
          n
        ),
    },
    {
      title: 'Date',
      dataIndex: 'request_date',
      key: 'request_date',
      render: (d: string) => (d ? String(d).slice(0, 10) : ''),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Priority', dataIndex: 'priority', key: 'priority' },
    { title: 'Source', dataIndex: 'source_type', key: 'source_type' },
    {
      title: 'Quotation',
      key: 'quotation',
      ellipsis: true,
      render: (_, r) => r.quotation?.quotation_number || '—',
    },
    {
      title: 'Customer',
      key: 'customer',
      ellipsis: true,
      render: (_, r) => r.customer?.customer_name || r.customer?.customer_code || '—',
    },
    {
      title: 'Requested by',
      key: 'requester',
      ellipsis: true,
      render: (_, r) => formatUser(r.requester) || '—',
    },
  ];

  if (routeId) {
    if (detailQ.isLoading) {
      return (
        <div style={{ padding: 24 }}>
      {routeId ? <AiPageContextSync module="manufacturing" docType="ProductionRequest" docId={routeId} /> : null}
          <Spin />
        </div>
      );
    }
    const pr = detailQ.data;
    if (!pr) {
      return (
        <div style={{ padding: 24 }}>
          <Text type="danger">Not found</Text>
        </div>
      );
    }

    const prDocs = docsQ.data ?? [];
    const hasApprovalPdf = prDocs.some((d) => d.document_type === PRODUCTION_APPROVAL_FORM_DOC_TYPE);
    const showApprovalGate = Boolean(pr.quotation_id) && EDITABLE_PR_STATUSES.includes(pr.status);
    const usedQuotationSet = new Set(usedQuotationIdsOnPrDetailQ.data ?? []);
    const quotationOptions = acceptedQuotations
      .filter((q) => !usedQuotationSet.has(q.id) || q.id === pr.quotation_id)
      .map((q) => ({
        value: q.id,
        label: `${q.quotation_number} — ${q.customer_name ?? q.quotation_name ?? ''}`.trim(),
      }));

    const waiverReasonDisplay = (() => {
      const r = pr.po_waiver_reason;
      if (!r) return '—';
      if (r === 'PO_will_follow_before_delivery') return 'PO will follow before delivery';
      if (r === 'Other') return pr.po_waiver_reason_detail ? `Other — ${pr.po_waiver_reason_detail}` : 'Other';
      return r;
    })();

    return (
      <div style={{ padding: 24, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {routeId ? <AiPageContextSync module="manufacturing" docType="ProductionRequest" docId={routeId} /> : null}
        <Button icon={<ArrowLeftOutlined />} type="link" onClick={() => navigate('/mfg/production-requests')}>
          Back to list
        </Button>
        <Space align="center" style={{ marginBottom: 8 }} wrap>
          <Title level={3} style={{ margin: 0 }}>
            {pr.request_number}
          </Title>
          {pr.parent_production_request ? (
            <Tag color="purple">Supplementary of {pr.parent_production_request.request_number}</Tag>
          ) : null}
          <Button
            type="default"
            icon={<PrinterOutlined />}
            onClick={() => navigate(`/mfg/production-requests/${pr.id}/approval-print`)}
          >
            Print approval form
          </Button>
        </Space>
        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Status">{pr.status}</Descriptions.Item>
          <Descriptions.Item label="Warehouse">{pr.warehouse?.warehouse_name || pr.warehouse_id}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {pr.department_id
              ? (() => {
                  const d = departments.find((x) => x.id === pr.department_id);
                  return d ? `${d.department_code} - ${d.department_name}` : pr.department_id;
                })()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {pr.customer?.customer_name
              ? `${pr.customer.customer_code ? `${pr.customer.customer_code} — ` : ''}${pr.customer.customer_name}`.trim()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Request date">{String(pr.request_date).slice(0, 10)}</Descriptions.Item>
          <Descriptions.Item label="Source">{pr.source_type}</Descriptions.Item>
          <Descriptions.Item label="Priority">{pr.priority}</Descriptions.Item>
          <Descriptions.Item label="Requested by">{formatUser(pr.requester) || '—'}</Descriptions.Item>
          {pr.quotation_id ? (
            <Descriptions.Item label="Quotation">
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => navigate(`/growth/quotations/${pr.quotation_id}`)}
              >
                {pr.quotation?.quotation_number ?? pr.quotation_id}
              </Button>
            </Descriptions.Item>
          ) : (
            <Descriptions.Item label="Quotation">—</Descriptions.Item>
          )}
          {pr.parent_production_request || pr.supplementary_requests?.length ? (
            <Descriptions.Item label="Related requests" span={2}>
              <Space size={4} wrap>
                {pr.parent_production_request ? (
                  <>
                    <Text type="secondary">Supplements</Text>
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => navigate(`/mfg/production-requests/${pr.parent_production_request!.id}`)}
                    >
                      {pr.parent_production_request.request_number}
                    </Button>
                    <Tag>{pr.parent_production_request.status}</Tag>
                  </>
                ) : null}
                {pr.supplementary_requests?.length ? (
                  <>
                    <Text type="secondary">Supplementary requests</Text>
                    {pr.supplementary_requests.map((s) => (
                      <Space key={s.id} size={2}>
                        <Button
                          type="link"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => navigate(`/mfg/production-requests/${s.id}`)}
                        >
                          {s.request_number}
                        </Button>
                        <Tag>{s.status}</Tag>
                      </Space>
                    ))}
                  </>
                ) : null}
              </Space>
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="External ref">{pr.external_ref_no || '—'}</Descriptions.Item>
          <Descriptions.Item label="Effective date">
            {pr.effective_date ? String(pr.effective_date).slice(0, 10) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Customer contact">{pr.contact_person || '—'}</Descriptions.Item>
          <Descriptions.Item label="PO waiver reason" span={2}>
            {waiverReasonDisplay}
          </Descriptions.Item>
          {pr.responsibility_notes ? (
            <Descriptions.Item label="Responsibility notes" span={2}>
              {pr.responsibility_notes}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Estimation sign-off" span={2}>
            {pr.signoff_estimation_at
              ? `${formatUser(pr.signoffEstimationUser ?? undefined) || '—'} @ ${formatDateTime(pr.signoff_estimation_at)}`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Operations sign-off" span={2}>
            {pr.signoff_operations_at
              ? `${formatUser(pr.signoffOperationsUser ?? undefined) || '—'} @ ${formatDateTime(pr.signoff_operations_at)}`
              : '—'}
          </Descriptions.Item>
          {pr.created_at ? (
            <Descriptions.Item label="Created" span={2}>
              {String(pr.created_at).replace('T', ' ').slice(0, 19)}
            </Descriptions.Item>
          ) : null}
          {pr.remarks ? (
            <Descriptions.Item label="Header remarks" span={2}>
              {pr.remarks}
            </Descriptions.Item>
          ) : null}
        </Descriptions>

        {EDITABLE_PR_STATUSES.includes(pr.status) ? (
          <Space style={{ marginBottom: 16 }} wrap>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                waiverForm.setFieldsValue({
                  quotation_id: pr.quotation_id,
                  external_ref_no: pr.external_ref_no,
                  effective_date: pr.effective_date ? dayjs(pr.effective_date) : null,
                  contact_person: pr.contact_person,
                  po_waiver_reason: (pr.po_waiver_reason as prApi.PoWaiverReason | null) ?? null,
                  po_waiver_reason_detail: pr.po_waiver_reason_detail,
                  responsibility_notes: pr.responsibility_notes,
                });
                setEditWaiverOpen(true);
              }}
            >
              Edit quotation / PO waiver
            </Button>
            <Button
              icon={<SolutionOutlined />}
              disabled={Boolean(pr.signoff_estimation_at) || !canSignoffEstimation}
              loading={detailAction === 'signoffEst'}
              title={
                !canSignoffEstimation
                  ? 'Requires Estimation sign-off permission'
                  : pr.signoff_estimation_at
                    ? 'Already signed'
                    : undefined
              }
              onClick={async () => {
                setDetailAction('signoffEst');
                try {
                  await prApi.signoffEstimationProductionRequest(pr.id);
                  message.success('Estimation sign-off recorded');
                  qc.invalidateQueries({ queryKey: ['production-request', pr.id] });
                } catch (e: unknown) {
                  message.error(apiActionErrorMessage(e));
                } finally {
                  setDetailAction(null);
                }
              }}
            >
              Sign as Estimation
            </Button>
            <Button
              icon={<DeploymentUnitOutlined />}
              disabled={Boolean(pr.signoff_operations_at) || !canSignoffOperations}
              loading={detailAction === 'signoffOps'}
              title={
                !canSignoffOperations
                  ? 'Requires Operations sign-off permission'
                  : pr.signoff_operations_at
                    ? 'Already signed'
                    : undefined
              }
              onClick={async () => {
                setDetailAction('signoffOps');
                try {
                  await prApi.signoffOperationsProductionRequest(pr.id);
                  message.success('Operations sign-off recorded');
                  qc.invalidateQueries({ queryKey: ['production-request', pr.id] });
                } catch (e: unknown) {
                  message.error(apiActionErrorMessage(e));
                } finally {
                  setDetailAction(null);
                }
              }}
            >
              Sign as Operations
            </Button>
          </Space>
        ) : null}

        {showApprovalGate && !hasApprovalPdf ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Signed production approval form required"
            description="This request is linked to a quotation. Upload a PDF with document type “Production approval form (signed PDF)” before submitting for approval."
          />
        ) : null}

        <Card size="small" title="Related documents" style={{ marginBottom: 16 }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <DocumentUpload
              entityType="ProductionRequest"
              entityId={pr.id}
              buttonText="Upload signed approval form (PDF)"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              For quotation-linked requests, choose document type Production approval form (signed PDF) so submit is allowed.
            </Text>
          </Space>
          <Table<Document>
            size="small"
            rowKey="id"
            loading={docsQ.isLoading}
            dataSource={prDocs}
            pagination={false}
            columns={[
              { title: 'Name', dataIndex: 'document_name', ellipsis: true },
              {
                title: 'Type',
                dataIndex: 'document_type',
                width: 220,
                render: (t: string | undefined) => t || '—',
              },
              {
                title: 'File',
                key: 'file',
                width: 100,
                render: (_: unknown, d: Document) =>
                  d.file_url ? (
                    <a href={d.file_url} target="_blank" rel="noopener noreferrer">
                      Open
                    </a>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
          <InheritedAttachmentsCard trailDomain="sales" entityType="ProductionRequest" entityId={pr.id} />
        </Card>

        <Table
          size="small"
          rowKey="id"
          dataSource={pr.lines || []}
          pagination={false}
          tableLayout="auto"
          columns={[
            { title: 'Line', dataIndex: 'line_number', width: 56 },
            {
              title: 'Product',
              key: 'item',
              onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 220 } }),
              render: (_: unknown, r: prApi.ProductionRequestLine) => {
                if (r.item?.item_code || r.item?.item_name) {
                  return `${r.item?.item_code ?? ''}${r.item?.item_code && r.item?.item_name ? ' — ' : ''}${r.item?.item_name ?? ''}`.trim();
                }
                if (r.manual_product_code || r.manual_product_description) {
                  return [r.manual_product_code, r.manual_product_description].filter(Boolean).join(' — ');
                }
                return r.item_id ? String(r.item_id) : '—';
              },
            },
            { title: 'Qty', dataIndex: 'quantity', width: 90 },
            { title: 'UOM', dataIndex: 'uom', width: 72 },
            {
              title: 'Target prod.',
              dataIndex: 'target_production_date',
              width: 110,
              render: (d: string | null) => (d ? String(d).slice(0, 10) : '—'),
            },
            { title: 'Stock', dataIndex: 'stock_type', width: 100 },
            {
              title: 'BOM',
              key: 'bom',
              width: 180,
              onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
              render: (_, r) => {
                if (r.bom?.bom_number || r.bom?.bom_name) {
                  return (
                    <span title={r.bom?.bom_name}>
                      {r.bom?.bom_number}
                      {r.bom?.bom_name ? ` — ${r.bom.bom_name}` : ''}
                    </span>
                  );
                }
                return r.bom_id ? <Tag title={r.bom_id}>Linked</Tag> : '—';
              },
            },
            {
              title: 'Project',
              key: 'project',
              onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
              render: (_, r) =>
                r.project?.project_number || r.project?.project_name
                  ? `${r.project?.project_number ? `${r.project.project_number} ` : ''}${r.project?.project_name ?? ''}`.trim()
                  : '—',
            },
            { title: 'Line priority', dataIndex: 'line_priority', width: 110, render: (x: string | null) => x || '—' },
            {
              title: 'Line remarks',
              dataIndex: 'remarks',
              onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
              render: (x: string | null) => x || '—',
            },
            { title: 'Released to WO', dataIndex: 'quantity_released_to_wo', width: 120 },
          ]}
        />
        <Space style={{ marginTop: 16 }} wrap>
          {pr.status === 'Draft' || pr.status === 'Returned' ? (
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={detailAction === 'submit'}
              disabled={
                (detailAction !== null && detailAction !== 'submit') ||
                (showApprovalGate && !docsQ.isLoading && !hasApprovalPdf)
              }
              onClick={async () => {
                setDetailAction('submit');
                try {
                  await prApi.submitProductionRequest(pr.id);
                  message.success('Submitted for approval');
                  qc.invalidateQueries({ queryKey: ['production-request', pr.id] });
                } catch (e: unknown) {
                  message.error(apiActionErrorMessage(e));
                } finally {
                  setDetailAction(null);
                }
              }}
            >
              Submit for approval
            </Button>
          ) : null}
          {pr.status === 'Pending_Approval' && pr.approval_instance_id ? (
            <div style={{ marginBottom: 16, width: '100%' }}>
              <ApprovalPanel
                entityType={ENTITY_TYPE_PRODUCTION_REQUEST}
                entityId={pr.id}
                extraInvalidateQueryKeys={[['production-request', pr.id], ['production-requests']]}
              />
            </div>
          ) : null}
          {pr.status === 'Pending_Approval' && !pr.approval_instance_id ? (
            <Space wrap>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 0 }}
                message="No workflow instance — approve with production-request permission."
              />
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={detailAction === 'approve'}
                disabled={!canApprovePr || (detailAction !== null && detailAction !== 'approve')}
                title={!canApprovePr ? 'Requires production request approve permission' : undefined}
                onClick={async () => {
                  setDetailAction('approve');
                  try {
                    await prApi.approveProductionRequest(pr.id);
                    message.success('Production request approved');
                    qc.invalidateQueries({ queryKey: ['production-request', pr.id] });
                    qc.invalidateQueries({ queryKey: ['production-requests'] });
                  } catch (e: unknown) {
                    message.error(apiActionErrorMessage(e));
                  } finally {
                    setDetailAction(null);
                  }
                }}
              >
                Approve
              </Button>
            </Space>
          ) : null}
          {pr.status === 'Approved' ? (
            <Button
              type="primary"
              icon={<ToolOutlined />}
              disabled={detailAction !== null}
              onClick={() => navigate(`/mfg/production-requests/${pr.id}/create-work-order`)}
            >
              Convert to Work Order
            </Button>
          ) : null}
        </Space>

        <Modal
          title="Edit quotation / PO waiver"
          open={editWaiverOpen}
          onCancel={() => setEditWaiverOpen(false)}
          confirmLoading={detailAction === 'saveWaiver'}
          okText="Save"
          onOk={async () => {
            try {
              const values = await waiverForm.validateFields();
              setDetailAction('saveWaiver');
              const reason =
                values.po_waiver_reason && String(values.po_waiver_reason).trim() !== ''
                  ? values.po_waiver_reason
                  : null;
              await prApi.updateProductionRequest(pr.id, {
                quotation_id: nilUuid(values.quotation_id),
                external_ref_no:
                  values.external_ref_no != null && String(values.external_ref_no).trim() !== ''
                    ? String(values.external_ref_no).trim()
                    : null,
                effective_date: values.effective_date ? dayjs(values.effective_date).format('YYYY-MM-DD') : null,
                contact_person:
                  values.contact_person != null && String(values.contact_person).trim() !== ''
                    ? String(values.contact_person).trim()
                    : null,
                po_waiver_reason: reason,
                po_waiver_reason_detail:
                  reason === 'Other' &&
                  values.po_waiver_reason_detail != null &&
                  String(values.po_waiver_reason_detail).trim() !== ''
                    ? String(values.po_waiver_reason_detail).trim()
                    : null,
                responsibility_notes:
                  values.responsibility_notes != null && String(values.responsibility_notes).trim() !== ''
                    ? String(values.responsibility_notes).trim()
                    : null,
              });
              message.success('Updated');
              setEditWaiverOpen(false);
              qc.invalidateQueries({ queryKey: ['production-request', pr.id] });
              qc.invalidateQueries({ queryKey: ['production-requests'] });
              qc.invalidateQueries({ queryKey: ['production-requests', 'used-quotation-ids'] });
              qc.invalidateQueries({ queryKey: ['documents', 'entity', 'ProductionRequest', pr.id] });
            } catch (e: unknown) {
              const err = e as { errorFields?: unknown };
              if (err.errorFields) return;
              message.error(apiActionErrorMessage(e));
            } finally {
              setDetailAction(null);
            }
          }}
          destroyOnClose
          width={640}
        >
          <Form form={waiverForm} layout="vertical">
            <Form.Item name="quotation_id" label="Quotation (accepted)">
              <Select allowClear showSearch optionFilterProp="label" placeholder="Optional" options={quotationOptions} />
            </Form.Item>
            <Form.Item name="external_ref_no" label="External reference">
              <Input placeholder="e.g. OPP-2026-0550" maxLength={100} />
            </Form.Item>
            <Form.Item name="effective_date" label="Effective date">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="contact_person" label="Customer contact">
              <Input maxLength={255} />
            </Form.Item>
            <Form.Item name="po_waiver_reason" label="PO waiver reason">
              <Select
                allowClear
                placeholder="Optional"
                options={[
                  { value: 'PO_will_follow_before_delivery', label: 'PO will follow before delivery' },
                  { value: 'Other', label: 'Other' },
                ]}
              />
            </Form.Item>
            {poWaiverReasonWatch === 'Other' ? (
              <Form.Item
                name="po_waiver_reason_detail"
                label="Other — detail"
                rules={[{ required: true, message: 'Enter detail when reason is Other' }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            ) : null}
            <Form.Item name="responsibility_notes" label="Responsibility notes">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {routeId ? <AiPageContextSync module="manufacturing" docType="ProductionRequest" docId={routeId} /> : null}
      <Space style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Production requests
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/mfg/production-requests/new')}>
          New request
        </Button>
        <Button icon={<DownloadOutlined />} onClick={exportListCsv} disabled={listQ.isLoading}>
          Export CSV
        </Button>
      </Space>
      <Card>
        <Table<prApi.ProductionRequest>
          loading={listQ.isLoading}
          rowKey="id"
          dataSource={listQ.data || []}
          columns={columns}
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => navigate(`/mfg/production-requests/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
}
